import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { X } from "lucide-react";
import React from "react";
import { UseFormReturn } from "react-hook-form";

export interface BillDaysThresholdFiltersType {
  threshold_check: string;
}

interface BillDaysThresholdFiltersProps {
  filters: BillDaysThresholdFiltersType;
  onFiltersChange: (filters: BillDaysThresholdFiltersType) => void;
  onApplyFilters: (filters: BillDaysThresholdFiltersType) => void;
  onClearFilters: () => void;
}

export const BillDaysThresholdFilters: React.FC<
  BillDaysThresholdFiltersProps
> = ({ filters, onFiltersChange, onApplyFilters, onClearFilters }) => {
  const createFormFields = (): FormField[] => [
    {
      name: "threshold_check",
      label: "Threshold Range",
      type: "select",
      required: false,
      placeholder: "All Thresholds",
      options: [
        { label: "All Thresholds", value: "all" },
        { label: "Less than 15 days", value: "0-15" },
        { label: "15-30 days", value: "15-30" },
        { label: "30-60 days", value: "30-60" },
        { label: "60+ days", value: "60+" },
      ],
      classes: FIELD_CLASSES,
      helperText:
        "Filter by service days between meter readings to identify billing period anomalies",
    },
  ];

  // Create form instance
  const form: UseFormReturn<BillDaysThresholdFiltersType> =
    FormService.createForm<BillDaysThresholdFiltersType>({
      fields: createFormFields(),
      defaultValues: filters,
    });

  // Handle form submission (Apply Filters)
  const handleSubmit = (data: BillDaysThresholdFiltersType) => {
    onApplyFilters(data);
  };

  // Handle form reset (Clear Filters)
  const handleReset = () => {
    const defaultFilters: BillDaysThresholdFiltersType = {
      threshold_check: "all",
    };

    form.reset(defaultFilters);
    onClearFilters();
  };

  // Custom footer with Apply and Clear buttons
  const renderCustomFooter = () => (
    <div className="flex justify-between items-center pt-6 border-t border-gray-200">
      <Button variant="outline" onClick={handleReset}>
        <X className="h-4 w-4" />
        Clear Filters
      </Button>

      <Button type="submit">Apply Filters</Button>
    </div>
  );

  return (
    <div className="space-y-4">
      <DynamicForm
        fields={createFormFields()}
        form={form}
        onSubmit={handleSubmit}
        onReset={handleReset}
        config={{
          showSubmitButton: false,
          showResetButton: false,
          layout: "vertical",
          spacing: "medium",
        }}
        gridColumns={1}
        className="space-y-6"
        renderCustomFooter={renderCustomFooter}
      />
    </div>
  );
};
